import React from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import { AdminBar, AdminBarHandle } from "components/AdminBar";
import { Button } from "components/Button";
import Line from "components/Line";
import Markdown from "components/Markdown";
import Menu from "components/Menu";
import OverviewMapBackground from "components/OverviewMapBackground";
import Seal from "components/Seal";
import Text from "components/Text";
import { SuperAdminFeature } from "components/privilegedUserFeatures";
import LinkLine from "components/utilities/LinkLine";
import useGetStarted from "hooks/useGetStarted";
import {
  getCallToAction,
  getDescription,
  getHighlightMessageMarkdown,
  getTitle,
  getType,
  isRequirementCatalog,
} from "reducers/guides";
import { guidePropType } from "utils/sharedPropTypes";

import Loader from "../Loader";
import AdminTools from "./AdminTools";
import styles from "./Guides.scss";

const GuideStartButton = ({ guide }) => {
  const getStarted = useGetStarted(guide);
  return (
    <div className={styles.guide}>
      <h3>{getTitle(guide)}</h3>
      <div className={styles.linkLineContainer}>
        <LinkLine />
      </div>
      <div className={styles.guideMessage}>{getDescription(guide)}</div>
      <StartButton guide={guide} callback={getStarted} />
    </div>
  );
};
GuideStartButton.propTypes = {
  guide: guidePropType,
};

const StartButton = ({ guide, callback }) => {
  if (!callback) return <Loader />;

  return (
    <Button
      expandOnMobile
      data-guide={getType(guide)}
      data-role="get-started"
      onClick={() => callback(guide)}
      label={getCallToAction(guide)}
      withSpinner
    />
  );
};
StartButton.propTypes = {
  guide: guidePropType,
  callback: PropTypes.func.isRequired,
};

const SingleGuideSplash = ({ title, message, guide, bounds }) => {
  const getStarted = useGetStarted(guide);
  return (
    <div className={styles.splashContainer}>
      <OverviewMapBackground bounds={bounds}>
        <AdminBarHandle className={styles.adminBarHandle} />
        <div className={styles.menu}>
          <Menu />
        </div>
        <div className={styles.content}>
          <span className={styles.seal}>
            <Seal type="light" style={{ maxHeight: "100px", maxWidth: "200px" }} />
          </span>
          <h1>{guide.splash_title || title}</h1>
          <p>{message}</p>
          <div className={styles.guideButton}>
            {getStarted ? (
              <Button
                expandOnMobile
                data-guide={guide.guide_type}
                data-role="get-started"
                onClick={getStarted}
                label={getCallToAction(guide)}
                withSpinner
              />
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </OverviewMapBackground>
    </div>
  );
};
SingleGuideSplash.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  guide: guidePropType.isRequired,
};

const MultiGuideSplash = ({ guides, bounds, tenantName, title }) => {
  const buttons = guides.map((guide) => (
    <GuideStartButton key={guide.id} withSpinner guide={guide} />
  ));

  return (
    <div className={styles.splashContainer}>
      <OverviewMapBackground bounds={bounds}>
        <AdminBarHandle className={styles.adminBarHandle} />
        <div className={styles.menu}>
          <Menu />
        </div>
        <div className={styles.content}>
          <span className={styles.seal}>
            <Seal type="light" style={{ maxHeight: "100px", maxWidth: "200px" }} />
          </span>
          <h1>{title || <Text t="pages.home.header" tenantName={tenantName} />}</h1>
          <SuperAdminFeature>
            <AdminTools />
          </SuperAdminFeature>
        </div>
      </OverviewMapBackground>
      <div className={styles.guideButtons}>{buttons}</div>
      <Line fullWidth noMargin />
    </div>
  );
};
MultiGuideSplash.propTypes = {
  guides: PropTypes.arrayOf(guidePropType).isRequired,
  tenantName: PropTypes.string,
  title: PropTypes.string,
};

const TopLevelGuides = ({ guides, ...props }) => {
  if (guides.length === 1) return <SingleGuideSplash guide={guides[0]} {...props} />;
  return <MultiGuideSplash guides={guides} {...props} />;
};
TopLevelGuides.propTypes = {
  guides: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const RequirementCatalogSplash = ({ guide }) => {
  const getStarted = useGetStarted(guide);

  return (
    <div className={styles.requirementCatalogSplash}>
      <Markdown source={getHighlightMessageMarkdown(guide) || ""} />
      <p>
        <StartButton guide={guide} callback={getStarted} />
      </p>
    </div>
  );
};
RequirementCatalogSplash.propTypes = {
  guide: guidePropType.isRequired,
};

const Guides = ({ guides, ...props }) => {
  const topLevelGuides = R.reject(isRequirementCatalog, guides);
  const requirementCatalogGuide = R.find(isRequirementCatalog, guides);

  return (
    <div className={styles.container}>
      <AdminBar />
      {!R.isEmpty(topLevelGuides) && <TopLevelGuides guides={topLevelGuides} {...props} />}
      {!R.isEmpty(topLevelGuides) && requirementCatalogGuide && (
        <RequirementCatalogSplash guide={requirementCatalogGuide} {...props} />
      )}
      {R.isEmpty(topLevelGuides) && requirementCatalogGuide && (
        <TopLevelGuides guides={[requirementCatalogGuide]} {...props} />
      )}
    </div>
  );
};
Guides.propTypes = {
  guides: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Guides;
